import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SEO from '@/components/SEO';
import { auth } from '@/utils/api';
import { toast } from 'sonner';
import { LogIn, X, Mail, Lock } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await auth.login(formData);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Login successful!');
      
      // Redirect based on user type
      const userType = response.data.user.userType;
      if (userType === 'admin') {
        navigate('/admin-dashboard');
      } else if (userType === 'artist') {
        navigate('/artist-dashboard');
      } else {
        navigate('/buyer-dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Login failed';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setSendingReset(true);
    try {
      const response = await auth.forgotPassword({ email: forgotEmail });
      toast.success(response.data.message || 'A new password has been sent to your email!');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to send password reset email';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to send password reset email');
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <div className="min-h-screen" style={{background: '#ffffff'}} data-testid="login-page">
      <SEO 
        title="Login"
        description="Login to your ARTCARDO account. Access your dashboard to manage purchases, track orders, or manage your artist profile."
        url="/login"
        noindex={true}
      />
      {/* Black header section with logo */}
      <div style={{background: '#000000', padding: '2rem 0', textAlign: 'center'}}>
        <Link to="/" className="inline-block" data-testid="logo-link-header">
          <img 
            src="/artcardo-logo.png" 
            alt="ARTCARDO Logo" 
            style={{height: '150px', width: 'auto', margin: '0 auto'}}
          />
        </Link>
      </div>

      {/* White content section */}
      <div className="flex items-center justify-center py-12">
        <div className="card max-w-md w-full mx-4" style={{border: '2px solid #000000'}}>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-2">Welcome Back</h2>
            <p className="text-gray-600" data-testid="login-subtitle">Please login to your account</p>
          </div>

        <form onSubmit={handleSubmit} data-testid="login-form">
          <div className="form-group">
            <label className="form-label">Email</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="form-input"
              data-testid="email-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              className="form-input"
              data-testid="password-input"
            />
          </div>

          {/* Forgot Password Link */}
          <div className="text-right mb-4">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-[#ef4444] hover:underline"
            >
              Forgot Password?
            </button>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full mb-4"
            style={{background: '#ef4444'}}
            data-testid="login-btn"
          >
            {loading ? 'Logging in...' : (
              <>
                <LogIn className="w-4 h-4 mr-2" />
                Login
              </>
            )}
          </Button>
        </form>

        <div className="text-center">
          <p className="text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#ef4444] font-semibold hover:underline" data-testid="register-link">
              Register here
            </Link>
          </p>
        </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-xl font-semibold">Reset Password</h2>
              <button 
                onClick={() => setShowForgotPassword(false)} 
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleForgotPassword} className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-[#ef4444]" />
                </div>
                <p className="text-gray-600">
                  Enter your registered email address. We'll send you a new temporary password.
                </p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <Input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={sendingReset}
                  className="flex-1"
                  style={{background: '#ef4444'}}
                >
                  {sendingReset ? 'Sending...' : 'Send New Password'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
